import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractService } from '../commons/abstract.service';
import { DateUtil } from '../helpers/date.util';
import { Attendance } from '../models/attendance.model';
import {
  AttendanceDashboardOverview,
  AttendanceGoalsConfig,
  AttendanceGoalsPayload,
  AttendanceLiveSnapshot,
} from '../models/dashboard-attendance.model';

@Injectable({ providedIn: 'root' })
export class DashboardService extends AbstractService<Attendance> {

  getURLBase(): string {
    return '/dashboard';
  }

  constructor(private http: HttpClient) {
    super(http);
  }

  preCreate(obj: Attendance): void {
    delete obj?._id;
  }
  preUpdate(obj: Attendance): void {
  }

  /**
   * The dashboard filters mean a calendar day, so only the day travels to the API
   * (`DateUtil.toCalendarDay`). Sending the raw `Date` sent the *instant* instead, and the API
   * derived the day from its UTC form — in America/Sao_Paulo that moved every window three hours
   * back, and after 21:00 local it already pointed at tomorrow.
   */
  getDashboardAttendancesKPIs(dateStart: string, dateEnd: string, department: string, attendant: string, channel: string): Observable<any> {
    const dayStart = DateUtil.toCalendarDay(dateStart);
    const dayEnd = DateUtil.toCalendarDay(dateEnd);
    return super.get(`/attendances/kpis?dateStart=${dayStart}&dateEnd=${dayEnd}&department=${department}&attendant=${attendant}&channel=${channel}`);
  }

  getAttendanceDashByHours(dateStart: string, dateEnd: string, department: string, attendant: string, channel: string): Observable<any> {
    const dayStart = DateUtil.toCalendarDay(dateStart);
    const dayEnd = DateUtil.toCalendarDay(dateEnd);
    return super.get(`/hours?&dateStart=${dayStart}&dateEnd=${dayEnd}&department=${department}&attendant=${attendant}&channel=${channel}`);
  }

  getStatusAttendance(status: string, dateStart: string, dateEnd: string, department: string, attendant: string, channel: string, page: number, limit: number) {
    const dayStart = DateUtil.toCalendarDay(dateStart);
    const dayEnd = DateUtil.toCalendarDay(dateEnd);
    return super.get(`/attendance/status?status=${status}&dateStart=${dayStart}&dateEnd=${dayEnd}&department=${department}&attendant=${attendant}&channel=${channel}&page=${page}&limit=${limit}`);
  }

  getVolumeMessageHours(hourStart: string, hourEnd: string) {
    return super.get(`/volume/message?hourStart=${hourStart}&hourEnd=${hourEnd}`)
  }

  getPeakAttendance(dateStart: string, dateEnd: string, department: string, attendant: string, channel: string): Observable<any> {
    const dayStart = DateUtil.toCalendarDay(dateStart);
    const dayEnd = DateUtil.toCalendarDay(dateEnd);
    return super.get(`/peak/attendance?dateStart=${dayStart}&dateEnd=${dayEnd}&department=${department}&attendant=${attendant}&channel=${channel}`);
  }

  getNewContact(dateStart: string, dateEnd: string, department: string, attendant: string, channel: string): Observable<any> {
    const dayStart = DateUtil.toCalendarDay(dateStart);
    const dayEnd = DateUtil.toCalendarDay(dateEnd);
    return super.get(`/new/contact?dateStart=${dayStart}&dateEnd=${dayEnd}&department=${department}&attendant=${attendant}&channel=${channel}`);
  }

  dateDashAttandanceOnline(dateStart: string, dateEnd: string, department: string, attendant: string, channel: string, page: number, limit: number): Observable<any> {
    const dayStart = DateUtil.toCalendarDay(dateStart);
    const dayEnd = DateUtil.toCalendarDay(dateEnd);
    return super.get(`/attendance/online?dateStart=${dayStart}&dateEnd=${dayEnd}&department=${department}&attendant=${attendant}&channel=${channel}&page=${Number(page)}&limit=${Number(limit)}`);
  }

  dateDashPeakNewContact(dateStart: string, dateEnd: string, department: string, attendant: string, channel: string): Observable<any> {
    const dayStart = DateUtil.toCalendarDay(dateStart);
    const dayEnd = DateUtil.toCalendarDay(dateEnd);
    return super.get(`/new/contact-user?dateStart=${dayStart}&dateEnd=${dayEnd}&department=${department}&attendant=${attendant}&channel=${channel}`);
  }

  /**
   * The live cards at the top of the attendance dashboard. No period travels with it on
   * purpose: a queue is a fact about this minute, not about the selected range.
   */
  getAttendancesLive(department: string, attendant: string, channel: string): Observable<AttendanceLiveSnapshot> {
    return super.getWithParams('/attendances/live', { department, attendant, channel });
  }

  /** Everything the attendance dashboard shows for the selected period. */
  getAttendancesOverview(dateStart: string, dateEnd: string, department: string, attendant: string, channel: string): Observable<AttendanceDashboardOverview> {
    return super.getWithParams('/attendances/overview', {
      dateStart: DateUtil.toCalendarDay(dateStart),
      dateEnd: DateUtil.toCalendarDay(dateEnd),
      department,
      attendant,
      channel,
    });
  }

  getAttendanceGoals(): Observable<AttendanceGoalsPayload> {
    return super.get('/goals');
  }

  saveAttendanceGoals(goals: AttendanceGoalsConfig): Observable<AttendanceGoalsPayload> {
    return super.put('/goals', goals);
  }

  /** Drop the company's own numbers and read the platform suggestions again. */
  resetAttendanceGoals(): Observable<AttendanceGoalsPayload> {
    return super.post('/goals/reset', {});
  }

  getOverallRatingAttendance(dateStart: string, dateEnd: string, department: string, attendant: string, channel: string): Observable<Record<string, any>> {
    const dayStart = DateUtil.toCalendarDay(dateStart);
    const dayEnd = DateUtil.toCalendarDay(dateEnd);
    return super.get(`/attendance/overall-rating?dateStart=${dayStart}&dateEnd=${dayEnd}&department=${department}&attendant=${attendant}&channel=${channel}`);
  }
}
